import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName, buildDVTSummaryLines } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

const NORMAL_STATUS = 'Normal - Compressible, patent';

const VEIN_LABELS = {
  commonFemoralVein: 'Common femoral vein',
  deepFemoralVein: 'Deep femoral vein',
  superficialFemoralVein: 'Femoral vein',
  poplitealVein: 'Popliteal vein',
  posteriorTibialVeins: 'Posterior tibial veins',
  peronealVeins: 'Peroneal veins',
  greatSaphenousVein: 'Great saphenous vein',
};

export function LegDVTPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const rl = data.rightLeg || {};
  const ll = data.leftLeg || {};

  const rightSummary = data.rightLegEvaluated ? buildDVTSummaryLines(VEIN_LABELS, rl, 'Right', 'lower') : null;
  const leftSummary = data.leftLegEvaluated ? buildDVTSummaryLines(VEIN_LABELS, ll, 'Left', 'lower') : null;

  const isVeinAbnormal = (vein) => {
    const s = (vein && vein.status) || '';
    return s !== '' && s !== NORMAL_STATUS;
  };

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="LOWER EXTREMITY DVT ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>FINDINGS SUMMARY</Text>
              {rightSummary && (
                <>
                  <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginTop: 4 }]}>Right Leg:</Text>
                  {rightSummary.map((line, i) => (
                    <Text key={i} style={[commonStyles.infoBoxText, { color: line.tone === 'abnormal' ? COLORS.abnormal : line.tone === 'normal' ? COLORS.normal : COLORS.black }]}>{line.text}</Text>
                  ))}
                </>
              )}
              {leftSummary && (
                <>
                  <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginTop: 6 }]}>Left Leg:</Text>
                  {leftSummary.map((line, i) => (
                    <Text key={i} style={[commonStyles.infoBoxText, { color: line.tone === 'abnormal' ? COLORS.abnormal : line.tone === 'normal' ? COLORS.normal : COLORS.black }]}>{line.text}</Text>
                  ))}
                </>
              )}
              {!rightSummary && !leftSummary && (
                <Text style={commonStyles.infoBoxText}>No legs evaluated</Text>
              )}
            </View>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {data.rightLegEvaluated && (
              <>
                <PdfAccentSectionHeader title="RIGHT LEG" />
                {Object.entries(VEIN_LABELS).map(([key, label]) => {
                  const vein = rl[key] || {};
                  return (
                    <View key={key}>
                      <PdfLabelValue
                        label={`${label}:`}
                        value={vein.status || ''}
                        isAbnormal={isVeinAbnormal(vein)}
                      />
                      {vein.notes && (
                        <View style={{ paddingLeft: 10 }}>
                          <PdfLabelValue label="Notes:" value={vein.notes} isAbnormal />
                        </View>
                      )}
                    </View>
                  );
                })}
              </>
            )}

            {data.leftLegEvaluated && (
              <>
                <PdfAccentSectionHeader title="LEFT LEG" />
                {Object.entries(VEIN_LABELS).map(([key, label]) => {
                  const vein = ll[key] || {};
                  return (
                    <View key={key}>
                      <PdfLabelValue
                        label={`${label}:`}
                        value={vein.status || ''}
                        isAbnormal={isVeinAbnormal(vein)}
                      />
                      {vein.notes && (
                        <View style={{ paddingLeft: 10 }}>
                          <PdfLabelValue label="Notes:" value={vein.notes} isAbnormal />
                        </View>
                      )}
                    </View>
                  );
                })}
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
